import { type JSX, useState } from 'react';

import { Button, Link } from 'Components/Buttons/Buttons';
import { DropdownItemClickType, PrimaryAndSecondaryDropdownButtonProps, SecondaryDropdownButton } from 'Components/Buttons/DropdownButton';
import { ComplianceRequirementControlSetStatusTooltip, ComplianceRequirementStatusIcon } from 'Components/ComplianceRequirementStatus/ComplianceRequirementStatus';
import { PageCell } from 'Components/Containers/PageCell/PageCell';
import { Text } from 'Components/Text/Text';
import { ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { getFrameworkGroupControlURL } from 'Helpers/URLBuilder/URLBuilder';
import { ControlSetDetails, StatusCategory } from 'Models/ComplianceRequirements';

import styles from './ModifyControlRow.module.css';

export const MaxControlsShown = 6; // Exported so this can be imported by tests.

export interface ModifyControlRowProps {
    mappedControlSet: ControlSetDetails;
    regulation: string;
    requirementId: string;
    openModifyControlSetModal: (selectedControlSet: string[]) => void;
    openDeleteControlSetModal: (selectedControlSet: string[]) => void;
}

export const ModifyControlRow = (props: ModifyControlRowProps): JSX.Element => {
    const [showAllControls, setShowAllControls] = useState(false);
    const requirementControlGroupDropdownProps: PrimaryAndSecondaryDropdownButtonProps = {
        dropdownItems: [
            {
                _type: DropdownItemClickType.ONCLICK,
                text: 'Modify Controls',
                onClick: () => props.openModifyControlSetModal(props.mappedControlSet.control_list.map((control) => control.control.identifier)),
                icon: ICON_EDIT_MODIFY_UPDATE,
            },
            {
                _type: DropdownItemClickType.ONCLICK,
                text: 'Delete Control Set',
                onClick: (): void => props.openDeleteControlSetModal(props.mappedControlSet.control_list.map((control) => getHumanReadableControlIdFromControl(control.control))),
                icon: ICON_DELETE_REMOVE,
            },
        ],
    };

    return (
        <>
            <div className={styles.modifyControlRow}>
                <div className={styles.controlGroupListing}>
                    <PageCell>
                        <div className={styles.controlGroupListingContent}>
                            <div className={styles.contentContainer}>
                                <Text variant="Header4">Do any of these controls meet the requirement?</Text>
                                <div className={styles.controlsContainer}>
                                    {props.mappedControlSet.control_list.slice(0, showAllControls ? undefined : MaxControlsShown).map((mappedControl, index) => {
                                        return (
                                            <div className={styles.controlContainer} key={index}>
                                                <div className={styles.iconContainer}>
                                                    <ComplianceRequirementStatusIcon regulation={props.regulation ?? ''} status={mappedControl.status} />
                                                </div>
                                                <div className={styles.dropdownButtonContainer}>
                                                    <Link size="sm" to={getFrameworkGroupControlURL(mappedControl.control.identifier)}>
                                                        {getHumanReadableControlIdFromControl(mappedControl.control)}
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {props.mappedControlSet.control_list.length > MaxControlsShown && (
                                    <div className={styles.showAllButton}>
                                        <Button variant="linkText" size="sm" onClick={() => setShowAllControls(!showAllControls)}>
                                            {!showAllControls ? 'Show All Mapped Controls' : 'Collapse Mapped Controls'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <div className={styles.dropdownButton}>
                                <SecondaryDropdownButton {...requirementControlGroupDropdownProps}>Modify Control Set</SecondaryDropdownButton>
                            </div>
                        </div>
                    </PageCell>
                </div>
                <div className={styles.groupOutcomeContainer}>
                    <PageCell>
                        <div className={styles.groupOutcome}>
                            <Text variant="Header4">Result</Text>
                            {props.mappedControlSet.status === undefined ? (
                                <div className={styles.groupResultTextContainer}>
                                    <div className={styles.resultIconContainer}>
                                        <Text variant="Header2" color="blue" noStyles>
                                            -
                                        </Text>
                                    </div>
                                    <Text variant="Header2" color="blue" noStyles>
                                        Awaiting Assessments
                                    </Text>
                                    <div className={styles.tooltip}>
                                        <ComplianceRequirementControlSetStatusTooltip regulation={props.regulation} requirementId={props.requirementId} status={props.mappedControlSet.status} />
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.groupResultTextContainer}>
                                    {/** Since the icon for StatusCategory.UNKNOWN is the same as the tooltip icon, this prevents showing them nearby to avoid confusion. The status text is sufficient. */}
                                    {props.mappedControlSet.status !== StatusCategory.UNKNOWN && (
                                        <div className={styles.resultIconContainer}>
                                            <ComplianceRequirementStatusIcon regulation={props.regulation} status={props.mappedControlSet.status} />
                                        </div>
                                    )}
                                    <Text variant="Header2" color="blue" noStyles>
                                        {props.mappedControlSet.status}
                                    </Text>
                                    <div className={styles.tooltip}>
                                        <ComplianceRequirementControlSetStatusTooltip regulation={props.regulation} requirementId={props.requirementId} status={props.mappedControlSet.status} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </PageCell>
                </div>
            </div>
        </>
    );
};
